"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hrComplianceDocumentsQuerySchema = exports.hrComplianceCredentialsQuerySchema = exports.hrEmployeeDocumentQuerySchema = exports.hrDocumentInvalidateSchema = exports.hrDocumentVerifySchema = exports.hrEmployeeDocumentReplaceSchema = exports.hrEmployeeDocumentCreateSchema = exports.hrCredentialQuerySchema = exports.hrCredentialRejectSchema = exports.hrCredentialVerifySchema = exports.hrEmployeeCredentialUpdateSchema = exports.hrEmployeeCredentialCreateSchema = exports.hrDocumentTypeQuerySchema = exports.hrDocumentTypeUpdateSchema = exports.hrDocumentTypeCreateSchema = exports.hrCredentialTypeQuerySchema = exports.hrCredentialTypeUpdateSchema = exports.hrCredentialTypeCreateSchema = exports.hrDocumentStatusEnum = exports.hrCredentialStatusEnum = exports.hrVerificationStatusEnum = exports.hrCredentialCategoryEnum = void 0;
const zod_1 = require("zod");
/**
 * HR credential + document contracts — Phase 1D (docs/HR_PHASE1_ARCHITECTURE.md
 * §7, §8).
 *
 * DECISION #3 (P0): expiry is a COMPLIANCE SIGNAL ONLY. Nothing in this file
 * (or the module it feeds) is consumed by a clinical-authorization check —
 * see the `hr-credential-no-clinical-coupling` guard test.
 */
exports.hrCredentialCategoryEnum = zod_1.z.enum([
    'MEDICAL_REGISTRATION', 'NURSING_REGISTRATION', 'PHARMACY_REGISTRATION', 'LAB_CERT',
    'RADIOLOGY_CERT', 'BLS', 'ACLS', 'SPECIALTY', 'HOSPITAL_PRIVILEGE', 'OTHER',
]);
exports.hrVerificationStatusEnum = zod_1.z.enum(['PENDING', 'VERIFIED', 'REJECTED']);
exports.hrCredentialStatusEnum = zod_1.z.enum(['ACTIVE', 'EXPIRED', 'SUPERSEDED', 'REVOKED']);
exports.hrDocumentStatusEnum = zod_1.z.enum(['ACTIVE', 'EXPIRED', 'REPLACED', 'INVALIDATED']);
// ── credential types (admin) ─────────────────────────────────────────────────
exports.hrCredentialTypeCreateSchema = zod_1.z.object({
    code: zod_1.z.string().trim().min(1).max(30),
    name: zod_1.z.string().trim().min(1).max(150),
    category: exports.hrCredentialCategoryEnum,
    requiresExpiry: zod_1.z.boolean().optional(),
    isClinical: zod_1.z.boolean().optional(),
});
exports.hrCredentialTypeUpdateSchema = exports.hrCredentialTypeCreateSchema.partial().extend({ isActive: zod_1.z.boolean().optional() });
exports.hrCredentialTypeQuerySchema = zod_1.z.object({ includeInactive: zod_1.z.coerce.boolean().optional() });
// ── document types (admin) ───────────────────────────────────────────────────
exports.hrDocumentTypeCreateSchema = zod_1.z.object({
    code: zod_1.z.string().trim().min(1).max(30),
    name: zod_1.z.string().trim().min(1).max(150),
    category: zod_1.z.string().trim().max(30).optional(),
    defaultExpiryReminderDays: zod_1.z.array(zod_1.z.coerce.number().int().positive()).max(10).optional(),
    requiresVerification: zod_1.z.boolean().optional(),
});
exports.hrDocumentTypeUpdateSchema = exports.hrDocumentTypeCreateSchema.partial().extend({ isActive: zod_1.z.boolean().optional() });
exports.hrDocumentTypeQuerySchema = zod_1.z.object({ includeInactive: zod_1.z.coerce.boolean().optional() });
// ── employee credentials ─────────────────────────────────────────────────────
exports.hrEmployeeCredentialCreateSchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid(),
    credentialTypeId: zod_1.z.string().uuid(),
    credentialNumber: zod_1.z.string().trim().max(100).optional(),
    issuingAuthority: zod_1.z.string().trim().max(255).optional(),
    issueDate: zod_1.z.coerce.date().optional(),
    expiryDate: zod_1.z.coerce.date().optional(),
    specialty: zod_1.z.string().trim().max(150).optional(),
    notes: zod_1.z.string().trim().max(2000).optional(),
});
exports.hrEmployeeCredentialUpdateSchema = exports.hrEmployeeCredentialCreateSchema
    .omit({ employeeId: true, credentialTypeId: true })
    .partial()
    .extend({ status: exports.hrCredentialStatusEnum.optional() });
exports.hrCredentialVerifySchema = zod_1.z.object({ notes: zod_1.z.string().trim().max(2000).optional() });
exports.hrCredentialRejectSchema = zod_1.z.object({ notes: zod_1.z.string().trim().min(3).max(2000) });
exports.hrCredentialQuerySchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid().optional(),
    status: exports.hrCredentialStatusEnum.optional(),
    verificationStatus: exports.hrVerificationStatusEnum.optional(),
    expiringWithinDays: zod_1.z.coerce.number().int().positive().max(3650).optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
// ── employee documents ───────────────────────────────────────────────────────
/** Multipart form fields alongside the uploaded file — the file itself is handled by `@core/upload`'s multer middleware, not this schema. */
exports.hrEmployeeDocumentCreateSchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid(),
    documentTypeId: zod_1.z.string().uuid(),
    documentNumber: zod_1.z.string().trim().max(100).optional(),
    issueDate: zod_1.z.coerce.date().optional(),
    expiryDate: zod_1.z.coerce.date().optional(),
    notes: zod_1.z.string().trim().max(2000).optional(),
});
exports.hrEmployeeDocumentReplaceSchema = zod_1.z.object({
    documentNumber: zod_1.z.string().trim().max(100).optional(),
    issueDate: zod_1.z.coerce.date().optional(),
    expiryDate: zod_1.z.coerce.date().optional(),
    notes: zod_1.z.string().trim().max(2000).optional(),
});
exports.hrDocumentVerifySchema = zod_1.z.object({ notes: zod_1.z.string().trim().max(2000).optional() });
exports.hrDocumentInvalidateSchema = zod_1.z.object({ notes: zod_1.z.string().trim().min(3).max(2000) });
exports.hrEmployeeDocumentQuerySchema = zod_1.z.object({
    status: exports.hrDocumentStatusEnum.optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
// ── compliance dashboard ─────────────────────────────────────────────────────
exports.hrComplianceCredentialsQuerySchema = zod_1.z.object({
    departmentId: zod_1.z.string().uuid().optional(),
    expiringWithinDays: zod_1.z.coerce.number().int().positive().max(3650).optional(),
    status: exports.hrCredentialStatusEnum.optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
exports.hrComplianceDocumentsQuerySchema = zod_1.z.object({
    departmentId: zod_1.z.string().uuid().optional(),
    expiringWithinDays: zod_1.z.coerce.number().int().positive().max(3650).optional(),
    status: exports.hrDocumentStatusEnum.optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
//# sourceMappingURL=hr-credential.schema.js.map